/*
	Fallback auth flow mechanism for authenticating with Facebook
*/

import { inject } from '../di'
import { ConfigService, RuntimeConfig } from '../ConfigService'
import { AccountsService } from '../AccountsService'
import { AnalyticsService } from '../AnalyticsService'
import { LocationService } from '../LocationService'
import {
    IAuthenticationService,
    AuthUser,
    AuthProviderUser,
    AuthStatus,
    LoginState,
} from '../definitions/IAuthenticationService'
import { AuthProvider } from '../../definitions/CommonDefinitions'
import { fetchJSON } from '../../utils/fetch'
import { parseHash } from '../../utils/URL'

const OAUTH_URL = 'https://www.facebook.com/v2.8/dialog/oauth'
const USER_PROFILE = 'https://graph.facebook.com/me'

export class FacebookRedirectAuthenticator implements IAuthenticationService {
    @inject accountsService: AccountsService
    @inject config: ConfigService
    @inject analytics: AnalyticsService
    @inject location: LocationService

    get status() {
        const loginCookie = Cookies.getJSON('flipp-user')
        return loginCookie ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT
    }

    get user() {
        const loginCookie = Cookies.get('flipp-user')
        return {
            info: JSON.parse(loginCookie),
        } as AuthUser
    }

    init = () => {
        return new Promise<void>((resolve, reject) => {
            if (window.location.pathname !== '/login_redirect') {
                resolve()
                return
            }

            const params = parseHash()

            let provider = -1
            try {
                const state = JSON.parse(params['state']) as LoginState
                provider = state.provider
            } catch (e) {
                console.log(e)
            }

            /* init() is called as by the Router as a global middleware. We can check the URL for a hash
             * and identify if id_token and access_token are provided. If they are, we know we are in the
             * middle of the oauth flow and can parse out the values. */
            if (provider === AuthProvider.FACEBOOK && params['access_token']) {
                // Construct a fake AuthProviderUser - we don't have name, email or image yet, but we only
                // need the accessToken and provider to fetch a token from Accounts API
                const user = {
                    id: '',
                    name: '',
                    email: '',
                    image: '',
                    accessToken: params['access_token'],
                    provider: AuthProvider.FACEBOOK,
                } as AuthProviderUser

                Promise.all<any>([this.accountsService.fetchToken(user)])
                    .then(([accountsUser]) => {
                        /* Updated the accounts info with the profile details*/
                        user.email = accountsUser.email

                        this._fetchUserProfile(params['access_token']).then(
                            (profileData) => {
                                const data = profileData as any
                                user.id = data.id || null
                                user.name = data.name || null
                                user.image = data.id
                                    ? `https://graph.facebook.com/${data.id}/picture`
                                    : null

                                /* Save two cookies:
                                 * 1) flipp-user: containing the user specific (ie AuthProviderUser interface)
                                 * 2) flipp-login: login info from Accounts API
                                 */
                                Cookies.set(
                                    'flipp-user',
                                    JSON.stringify(user),
                                    {
                                        expires: 30,
                                        domain:
                                            this.config.ENV === 'development'
                                                ? 'localhost'
                                                : '.flipp.com',
                                    }
                                )
                                Cookies.set(
                                    'flipp-login',
                                    JSON.stringify({ token: accountsUser }),
                                    {
                                        expires: 30,
                                        domain:
                                            this.config.ENV === 'development'
                                                ? 'localhost'
                                                : '.flipp.com',
                                    }
                                )
                                resolve()
                            }
                        )
                    })
                    .catch((err: Error) => {
                        reject(err)
                    })
            } else {
                resolve()
            }
        })
    }

    login = () => {
        return Promise.resolve().then(() => {
            const redirectUrl = `${window.location.protocol}//${window.location.host}/login_redirect`
            const pageLink = window.location.pathname + window.location.search

            const params = this.analytics.analyticsParameters
            const paramsArr = Object.getOwnPropertyNames(params).map(
                (key) => `${key}=${encodeURIComponent(params[key])}`
            )
            paramsArr.push(`fsa=${this.location.postalCode}`)
            paramsArr.push(`locale=${this.location.locale}`)
            paramsArr.push(`context=${encodeURIComponent(pageLink)}`)
            if (document.documentElement.getAttribute('native')) {
                paramsArr.push('exp=native')
            }

            /* If we're in distribution, set redirect path to /dist so that beacons can be properly configured after
             * redirect. Including `context` parameter so we can redirect the user to the correct page */
            const path =
                this.config.RUNTIME === RuntimeConfig.DISTRIBUTION
                    ? `/dist?${paramsArr.join('&')}`
                    : window.location.pathname + window.location.search

            const state = JSON.stringify({
                provider: AuthProvider.FACEBOOK,
                path,
            })
            const authUrl = `${OAUTH_URL}?client_id=${this.config.FACEBOOK_APP_ID}&redirect_uri=${redirectUrl}&state=${encodeURIComponent(state)}&response_type=token&scope=email,public_profile`
            window.location.href = authUrl
        })
    }

    logout = () => {
        return Promise.resolve().then(() => {
            Cookies.remove('flipp-login', {
                domain:
                    this.config.ENV === 'development'
                        ? 'localhost'
                        : '.flipp.com',
            })
            Cookies.remove('flipp-user', {
                domain:
                    this.config.ENV === 'development'
                        ? 'localhost'
                        : '.flipp.com',
            })
        })
    }

    private _fetchUserProfile = (token: string) => {
        const url = `${USER_PROFILE}?fields=name,email&access_token=${token}`
        return fetchJSON(url)
            .then((profile) => {
                return profile
            })
            .catch(() => {
                /* An error here is not fatal - simply catch it and return null. The UI will not be completely
                 * populated (eg: no user image), but we have the email from Accounts API and that's enough.
                 * Firefox Private mode blocks ALL access to the Facebook Graph API and so this error is EXPECTED
                 * in that scenario. */
                return null
            })
    }
}
